/* eslint-disable import/prefer-default-export */
import * as React from "react";
import Script from "next/script";
import type Rollbar from "rollbar";

import useLogger from "../../../hooks/useLogger";
import useMutationObserver from "../../hooks/useMutationObserver";

const { createContext, useCallback, useContext, useEffect, useRef } = React;

/** **NOTE**: If Affirm changes the default financing copy,
 * the number of elements may change.
 * Please verify this first if this breaks.
 */
export const AFFIRM_NODE_COUNT = 6;
export const AFFIRM_CLASSES = {
  logo: "__affirm-logo",
  oldLogoColor: "__affirm-logo-black",
  newLogoColor: "text-charcoal",
  modalTrigger: "affirm-modal-trigger",
};

interface AffirmPromoProps {
  amount: string;
  v2?: boolean;
}

const updateMessaging = (element: HTMLElement, logger: Rollbar) => {
  const logo = element.querySelector(`.${AFFIRM_CLASSES.logo}`);
  if (!logo) {
    logger.warn("Could not find logo element in Affirm HTML");
    return;
  }
  logo.classList.remove(AFFIRM_CLASSES.oldLogoColor);
  logo.classList.add(AFFIRM_CLASSES.newLogoColor);

  const modalTrigger = element.querySelector(`.${AFFIRM_CLASSES.modalTrigger}`);
  if (!modalTrigger) {
    logger.warn("Could not find modalTrigger element in Affirm HTML");
    return;
  }
  modalTrigger.innerHTML = "ⓘ";
  modalTrigger.innerHTML +=
    "<style>.affirm-modal-trigger {font-size: 12px;}</style>";

  // eslint-disable-next-line no-param-reassign
  element.innerHTML = "";

  const span = document.createElement("span");
  span.innerHTML = " financing available ";
  span.style.fontSize = "12px";

  element.appendChild(logo);
  element.appendChild(span);
  element.appendChild(modalTrigger);
};

export const AffirmContext = createContext<string>("");

/**
 * Displays Affirm financing promotional messaging
 */
export function AffirmPromo({ amount, v2 }: AffirmPromoProps) {
  const affirmPlacement = useRef<HTMLElement>();
  const amountInCents = parseFloat(amount) * 100;
  const affirmKey = useContext(AffirmContext);
  const { logger } = useLogger();

  useEffect(() => {
    const client = window.affirm;
    const isReady = client?.jsReady?.();
    if (affirmPlacement.current && isReady) {
      client.ui.refresh();
    }
  }, [amount]);

  useMutationObserver(affirmPlacement, (mutations) => {
    mutations.forEach(({ target, addedNodes }) => {
      if (v2 || !(target instanceof HTMLElement)) return;
      if (Array.from(addedNodes).length !== AFFIRM_NODE_COUNT) return;

      affirmPlacement.current = target;
      updateMessaging(affirmPlacement.current, logger);
    });
  });

  const setRef = useCallback((element: HTMLDivElement) => {
    affirmPlacement.current = element;
  }, []);

  return (
    <>
      {affirmKey ? (
        <Script id="affirm">
          {`
        _affirm_config = {
        public_api_key:  "${affirmKey}",
        script:          "https://cdn1.affirm.com/js/v2/affirm.js"
      };
      (function(m,g,n,d,a,e,h,c){var b=m[n]||{},k=document.createElement(e),p=document.getElementsByTagName(e)[0],l=function(a,b,c){return function(){a[b]._.push([c,arguments])}};b[d]=l(b,d,"set");var f=b[d];b[a]={};b[a]._=[];f._=[];b._=[];b[a][h]=l(b,a,h);b[c]=function(){b._.push([h,arguments])};a=0;for(c="set add save post open empty reset on off trigger ready setProduct".split(" ");a<c.length;a++)f[c[a]]=l(b,d,c[a]);a=0;for(c=["get","token","url","items"];a<c.length;a++)f[c[a]]=function(){};k.async=
      !0;k.src=g[e];p.parentNode.insertBefore(k,p);delete g[e];f(g);m[n]=b})(window,_affirm_config,"affirm","checkout","ui","script","ready","jsReady");
    `}
        </Script>
      ) : null}
      <div
        ref={setRef}
        data-testid="affirm-promo"
        className="affirm-as-low-as"
        data-amount={amountInCents}
        data-page-type="product"
        data-affirm-color="black"
      />
    </>
  );
}
